import { FC, useEffect, useState } from 'react';
import { Box } from '@hopskipdrive/react-components';

import SvgPersonFilledIcon from '../../../../shared/components/Icons/PersonFilledIcon';
import { PhotoOrInitials } from '../../../../shared/components/PhotoOrInitials';
import { colorTokens } from '../../../../shared/components/variables';
import { ErrorBoundary } from '../../../../shared/utils/errorReporting/ErrorBoundary';
import { getFirstNameLastInitial } from '../../../../shared/utils/helpers/getName';
import { RemovedRider, RideOrganizer, RiderOnRide } from '../../../types/rides';
import { getLeadOrganizerName } from '../../../utils/helpers/organizer';
import { RemovedRiderAssistantDetails, RiderAssistantDetails } from '../../RiderAssistantDetails';
import { SkeletonRiderAssistantDetails } from '../../RiderAssistantDetails/RiderAssistantPresenter';
import { InfoNavContent } from '..';

import { RiderDetails, RiderDetailsSkeleton } from './RiderDetails';
import { AvatarContainer, RemovedRiderTab, RiderAssistantTab, RiderTab, RiderTabs } from './RiderDetailsTab.styles';

interface RiderDetailsTabProps {
  organizers: RideOrganizer[];
  riders: RiderOnRide[];
  removedRiders: RemovedRider[];
  riderAssistant?: RiderOnRide;
  adminRideUrl: string;
  removedRiderAssistants?: RemovedRider[];
  isCarePartnerRide?: boolean;
}

const NO_RIDER_ASSISTANT = 'no-rider-assistant';

export const RiderDetailsTab: FC<RiderDetailsTabProps> = ({
  organizers,
  riders,
  removedRiders,
  riderAssistant,
  adminRideUrl,
  removedRiderAssistants = [],
  isCarePartnerRide = false,
}) => {
  const selectedRider = riders?.length === 1 ? riders[0].slug : '';
  const selectedRemovedRider = !riders?.length && removedRiders?.length === 1 ? removedRiders[0].slug : '';
  const [selectedTab, setSelectedTab] = useState<string>(selectedRider || selectedRemovedRider);
  const leadOrganizerName = organizers?.length && getLeadOrganizerName(organizers);
  const isRiderAssistantSelected = selectedTab === riderAssistant?.id?.toString() || selectedTab === NO_RIDER_ASSISTANT;
  const isRemovedRiderAssistantSelected = removedRiderAssistants.find(
    (removedRider) => removedRider.id.toString() === selectedTab
  );
  const isRemovedRider = !!removedRiders.find((rider) => rider.slug === selectedTab);
  const riderList = isRemovedRider ? removedRiders : riders;
  const profileUrl = riderList.find((rider) => rider.slug === selectedTab)?.profileUrl || '';

  useEffect(() => {
    !isCarePartnerRide && isRiderAssistantSelected && setSelectedTab(selectedRider);
  }, [isCarePartnerRide]);

  return (
    <>
      <Box
        style={{
          borderTop: `1px solid ${colorTokens.primary.borderDark}`,
          background: colorTokens.tonal.light,
          width: '100%',
          padding: '8px 6px 8px 19px',
        }}
      >
        <TabsPresenter
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          riders={riders}
          removedRiders={removedRiders}
          riderAssistant={riderAssistant}
          isCarePartnerRide={isCarePartnerRide}
          removedRiderAssistants={removedRiderAssistants}
        />
      </Box>
      {isRiderAssistantSelected && (
        <InfoNavContent>
          <ErrorBoundary FallbackComponent={SkeletonRiderAssistantDetails}>
            <RiderAssistantDetails adminRideUrl={adminRideUrl} riderAssistantId={selectedTab} />
          </ErrorBoundary>
        </InfoNavContent>
      )}
      {isRemovedRiderAssistantSelected && (
        <InfoNavContent>
          <ErrorBoundary FallbackComponent={SkeletonRiderAssistantDetails}>
            <RemovedRiderAssistantDetails adminRideUrl={adminRideUrl} riderAssistantId={selectedTab} />
          </ErrorBoundary>
        </InfoNavContent>
      )}
      {!isRiderAssistantSelected && !isRemovedRiderAssistantSelected && selectedTab && (
        <InfoNavContent>
          <ErrorBoundary
            FallbackComponent={() => <RiderDetailsSkeleton organizerName={leadOrganizerName} riderSlug={selectedTab} />}
          >
            <RiderDetails
              isRemovedRider={!!removedRiders.find((rider) => rider.slug === selectedTab)}
              riderSlug={selectedTab}
              profileUrl={profileUrl}
              organizerName={leadOrganizerName || ''}
              isCarePartnerRide={isCarePartnerRide}
            />
          </ErrorBoundary>
        </InfoNavContent>
      )}
    </>
  );
};

interface TabsPresenterProps {
  selectedTab: string;
  setSelectedTab: (tab: string) => void;
  riders: RiderOnRide[];
  removedRiders: RemovedRider[];
  isCarePartnerRide?: boolean;
  riderAssistant?: RiderOnRide;
  removedRiderAssistants?: RemovedRider[];
}

// presenter component for easier testing and separation of concerns
export const TabsPresenter: FC<TabsPresenterProps> = ({
  selectedTab,
  setSelectedTab,
  riders,
  removedRiders,
  riderAssistant,
  isCarePartnerRide = false,
  removedRiderAssistants = [],
}) => {
  return (
    <RiderTabs
      value={selectedTab}
      sx={{
        pointerEvents: 'all',
        '& .MuiTabs-flexContainer': {
          gap: '16px',
        },
      }}
    >
      {/* The CarePartner tabs must be rendered separate without any additional wrapper container so Tabs can add additional props and classes*/}
      {isCarePartnerRide && (
        <RiderAssistantTab
          icon={
            riderAssistant?.slug ? (
              <PhotoOrInitials
                pictureUrl={riderAssistant?.profilePictureUrl || ''}
                key={`riderAssistant-${riderAssistant?.slug}`}
                fullName={`${riderAssistant?.firstName} ${riderAssistant?.lastName}`}
                size={33}
                fontSize={14}
              />
            ) : (
              <AvatarContainer>
                <SvgPersonFilledIcon />
              </AvatarContainer>
            )
          }
          label={`RA: ${
            riderAssistant?.slug
              ? getFirstNameLastInitial(`${riderAssistant?.firstName} ${riderAssistant?.lastName}`)
              : 'n/a'
          }`}
          value={riderAssistant?.slug || NO_RIDER_ASSISTANT}
          onClick={() => setSelectedTab(riderAssistant?.id?.toString() || NO_RIDER_ASSISTANT)}
        />
      )}
      {isCarePartnerRide &&
        removedRiderAssistants?.map((removedRiderAssistant) => (
          <RemovedRiderTab
            key={removedRiderAssistant.slug}
            icon={
              <PhotoOrInitials
                pictureUrl={removedRiderAssistant.profilePictureUrl || ''}
                key={`removedRiderAssistant-${removedRiderAssistant.slug}`}
                fullName={`${removedRiderAssistant.firstName} ${removedRiderAssistant.lastName}`}
                size={33}
                fontSize={14}
              />
            }
            label={`RA: ${getFirstNameLastInitial(
              `${removedRiderAssistant.firstName} ${removedRiderAssistant.lastName}`
            )}`}
            value={removedRiderAssistant.slug}
            onClick={() => setSelectedTab(removedRiderAssistant.id.toString())}
          />
        ))}
      {riders?.map((rider) => (
        <RiderTab
          key={rider.slug}
          icon={
            <PhotoOrInitials
              pictureUrl={rider.profilePictureUrl || ''}
              key={`rider-${rider.slug}`}
              fullName={`${rider.firstName} ${rider.lastName}`}
              size={33}
              fontSize={14}
            />
          }
          label={getFirstNameLastInitial(`${rider.firstName} ${rider.lastName}`)}
          value={rider.slug}
          onClick={() => setSelectedTab(rider.slug)}
        />
      ))}
      {removedRiders?.map((removedRider) => (
        <RemovedRiderTab
          key={removedRider.slug}
          icon={
            <PhotoOrInitials
              pictureUrl={removedRider.profilePictureUrl || ''}
              key={`removedRider-${removedRider.slug}`}
              fullName={`${removedRider.firstName} ${removedRider.lastName}`}
              size={33}
              fontSize={14}
            />
          }
          label={getFirstNameLastInitial(`${removedRider.firstName} ${removedRider.lastName}`)}
          value={removedRider.slug}
          onClick={() => setSelectedTab(removedRider.slug)}
        />
      ))}
    </RiderTabs>
  );
};
